<script setup lang="ts">
import {computed, ref, watch, onMounted} from 'vue';
import Translator from '@/utils/common/Translator';
import {useCrossFilterStore} from '@/stores/crossFilterStore';
import {getMappingTypeShape, type BIMappingSlot} from './mappingTypeShape';
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;
import BIWidgetSlotOverride = namaTypes.BIWidgetSlotOverride;
import DTOBIWizardFieldOption = namaTypes.DTOBIWizardFieldOption;

const props = defineProps<{
  widgetId: string;
  info: BIWidgetRuntimeSelectorInfo;
}>();

const emit = defineEmits<{
  (e: 'applied', override: BIWidgetSlotOverride): void;
  (e: 'reset'): void;
  (e: 'close'): void;
}>();

const crossFilterStore = useCrossFilterStore();

const wizardFieldOptions = computed<DTOBIWizardFieldOption[]>(() => props.info.fieldOptions ?? []);
const shape = computed(() => getMappingTypeShape(props.info.mappingType));
const currentOverride = computed(() => crossFilterStore.getWidgetSlotOverride(props.widgetId));

const draft = ref<BIWidgetSlotOverride>({widgetId: props.widgetId});

function initDraftFromCurrent() {
  const stored = crossFilterStore.getWidgetSlotOverride(props.widgetId);
  if (stored) {
    draft.value = {...stored};
    return;
  }
  const info = props.info;
  draft.value = {
    widgetId: props.widgetId,
    categoryWizardFieldId: info.categoryWizardFieldId,
    labelWizardFieldId: info.labelWizardFieldId,
    valueWizardFieldId: info.valueWizardFieldId,
    xWizardFieldId: info.xWizardFieldId,
    yWizardFieldId: info.yWizardFieldId,
    sizeWizardFieldId: info.sizeWizardFieldId,
    measureWizardFieldIds: info.measureWizardFieldIds ? [...info.measureWizardFieldIds] : undefined,
  };
}

onMounted(initDraftFromCurrent);
watch(() => props.widgetId, initDraftFromCurrent);

function optionLabel(opt: DTOBIWizardFieldOption): string {
  const arabic = Translator.isArabic();
  const preferred = arabic ? (opt.arabicTitle || opt.englishTitle) : (opt.englishTitle || opt.arabicTitle);
  const translated = preferred || Translator.translateId(opt.fieldId);
  if (translated && translated !== opt.fieldId)
    return `${translated} (${opt.fieldId})`;
  return opt.fieldId;
}

function optionsForSlot(slot: BIMappingSlot): Array<{label: string; value: string; disable?: boolean}> {
  const currentValueForThisSlot = (draft.value as any)[slot.wizardFieldIdKey] as string | undefined;
  const pickedDimensionsInOtherSlots = new Set<string>();
  if (slot.kind === 'dimension' && shape.value) {
    for (const other of shape.value.slots) {
      if (other === slot || other.kind !== 'dimension') continue;
      const picked = (draft.value as any)[other.wizardFieldIdKey] as string | undefined;
      if (picked) pickedDimensionsInOtherSlots.add(picked);
    }
  }
  return wizardFieldOptions.value
    .filter(wf => (wf.chartUsage ?? '').toLowerCase() === slot.kind)
    .map(wf => ({
      label: optionLabel(wf),
      value: wf.fieldId,
      disable: slot.kind === 'dimension' && wf.fieldId !== currentValueForThisSlot && pickedDimensionsInOtherSlots.has(wf.fieldId),
    }));
}

function measureOptions(): Array<{label: string; value: string}> {
  return wizardFieldOptions.value
    .filter(wf => (wf.chartUsage ?? '').toLowerCase() === 'measure')
    .map(wf => ({label: optionLabel(wf), value: wf.fieldId}));
}

const dimensionSlotsVisible = computed(() => {
  if (!shape.value) return [];
  if (props.info.disableRuntimeDimensionSelection) return [];
  return shape.value.slots.filter(s => s.kind === 'dimension');
});

const measureSlotsVisible = computed(() => {
  if (!shape.value) return [];
  if (props.info.disableRuntimeMeasureSelection) return [];
  return shape.value.slots.filter(s => s.kind === 'measure');
});

const showsMultiMeasure = computed(() => {
  if (!shape.value || props.info.disableRuntimeMeasureSelection) return false;
  return shape.value.supportsMultiMeasureSeries && !!props.info.flexibleCategoryValueSeries;
});

function applyNow() {
  crossFilterStore.setWidgetSlotOverride(props.widgetId, draft.value);
  emit('applied', {...draft.value});
}

function onDimensionPicked(slot: BIMappingSlot, v: string) {
  (draft.value as any)[slot.wizardFieldIdKey] = v;
  applyNow();
}

function onMeasurePicked(slot: BIMappingSlot, v: string) {
  (draft.value as any)[slot.wizardFieldIdKey] = v;
  applyNow();
}

function onMultiMeasurePicked(v: string[]) {
  draft.value.measureWizardFieldIds = v;
  applyNow();
}

function reset() {
  crossFilterStore.resetWidgetSlotOverride(props.widgetId);
  emit('reset');
  emit('close');
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-80 tw-p-3 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-text-gray-900 dark:tw-text-gray-100">
    <div class="tw-flex tw-items-center tw-justify-between">
      <span class="tw-font-semibold tw-text-sm">{{ Translator.translate('biRuntimeSlotSelection') }}</span>
      <q-chip clickable dense size="sm" icon="refresh" :label="Translator.translate('biResetRuntimeSelection')"
              :disable="!currentOverride" @click="reset"/>
    </div>

    <template v-if="!shape">
      <div class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">—</div>
    </template>

    <template v-else>
      <q-select v-for="slot in dimensionSlotsVisible" :key="'dim-' + slot.slotId"
                outlined dense hide-bottom-space emit-value map-options
                :label="Translator.translate(slot.labelKey)"
                :options="optionsForSlot(slot)"
                option-label="label" option-value="value"
                :model-value="(draft as any)[slot.wizardFieldIdKey]"
                @update:model-value="(v: string) => onDimensionPicked(slot, v)"
                class="nama-customize-dense-input nama-focused-input nama-field-input"
                input-class="nama-input-class"/>

      <q-select v-for="slot in measureSlotsVisible" :key="'msr-' + slot.slotId"
                outlined dense hide-bottom-space emit-value map-options
                :label="Translator.translate(slot.labelKey)"
                :options="optionsForSlot(slot)"
                option-label="label" option-value="value"
                :clearable="slot.optional"
                :model-value="(draft as any)[slot.wizardFieldIdKey]"
                @update:model-value="(v: string) => onMeasurePicked(slot, v)"
                class="nama-customize-dense-input nama-focused-input nama-field-input"
                input-class="nama-input-class"/>

      <q-select v-if="showsMultiMeasure" multiple outlined dense hide-bottom-space emit-value map-options use-chips
                :label="Translator.translate('biMeasures')"
                :options="measureOptions()" option-label="label" option-value="value"
                :model-value="draft.measureWizardFieldIds ?? []"
                @update:model-value="(v: string[]) => onMultiMeasurePicked(v)"
                class="nama-customize-dense-input nama-focused-input nama-field-input"
                input-class="nama-input-class"/>
    </template>
  </div>
</template>
